/**
 * Pure translation resolver. No React, so it is trivially testable.
 *
 * Fallback chain (never renders a raw dotted key or a blank):
 *   1. the active locale's catalog
 *   2. the English base catalog (so an unsupported/partial locale degrades to English)
 *   3. a humanized form of the key's last segment (developer-error safety net)
 */
import { en } from "./catalogs/en";
import { es } from "./catalogs/es";
import { fr } from "./catalogs/fr";
import { DEFAULT_LOCALE, type Locale, type Messages } from "./types";

export const CATALOGS: Partial<Record<Locale, Messages>> = { en, es, fr };

export function humanizeKey(key: string): string {
  const last = key.split(".").pop() ?? key;
  const spaced = last
    .replace(/[-_]/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .trim();
  if (!spaced) return key;
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export function translate(
  locale: Locale,
  key: string,
  vars?: Record<string, string | number>,
): string {
  const active = CATALOGS[locale] ?? {};
  const base   = CATALOGS[DEFAULT_LOCALE] ?? {};

  let value = active[key];
  if (value === undefined) value = base[key];        // fall back to English
  if (value === undefined) value = humanizeKey(key); // never a raw key or blank

  if (vars) {
    // Interpolate {name} tokens; an unknown token is left visible as {name}, never dropped.
    value = value.replace(/\{(\w+)\}/g, (_m, name: string) =>
      name in vars ? String(vars[name]) : `{${name}}`,
    );
  }
  return value;
}
